import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Check,
    Copy,
    ExternalLink,
    File,
    Folder,
    Link,
    Loader2,
    Plus,
    RefreshCw,
} from 'lucide-react';
import Modal from './Modal';
import { Checkbox } from './FieldComponents';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import '../styles/Customs.css';
import '../styles/CaseShareLinksModal.css';

const defaultApiOrigin = () => {
    const configured = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (configured) return configured;
    if (typeof window === 'undefined') return '';
    const { protocol, hostname, origin } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `https://api.casedb.co`;
    } else if (!hostname || hostname.startsWith('api.')) {
        return origin;
    }
    const labels = hostname.split('.').filter(Boolean);
    const applicationRoot = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
    return `${protocol}//api.${applicationRoot}`;
};

const asBoolean = (value) => value === true || value === 1 || value === '1' || value === 'true';

const numberOrZero = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const parseCaseDbDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return new Date(Number.NaN);
    const sqlTimestamp = raw.match(
        /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?$/,
    );
    if (!sqlTimestamp) return new Date(raw);
    const milliseconds = sqlTimestamp[3]
        ? `.${sqlTimestamp[3].slice(0, 3).padEnd(3, '0')}`
        : '';
    return new Date(`${sqlTimestamp[1]}T${sqlTimestamp[2]}${milliseconds}Z`);
};

const isExpiredByDate = (expiresAt) => {
    if (!expiresAt) return false;
    const parsed = parseCaseDbDate(expiresAt);
    return !Number.isNaN(parsed.getTime()) && parsed.getTime() <= Date.now();
};

const normalizeLink = (link = {}) => {
    const includesFutureUploads = asBoolean(link.include_future_uploads);
    const selectionMode = String(link.selection_mode || (includesFutureUploads ? 'live' : 'snapshot')).toLowerCase();
    return {
        ...link,
        id: link.id,
        token: String(link.token || ''),
        share_path: String(link.share_path || ''),
        location: String(link.location || 'Case documents'),
        is_folder: asBoolean(link.is_folder),
        expired: link.expired === undefined ? isExpiredByDate(link.expires_at) : asBoolean(link.expired),
        allow_upload: asBoolean(link.allow_upload),
        can_manage_uploads: asBoolean(link.can_manage_uploads),
        include_future_uploads: includesFutureUploads,
        can_freeze: asBoolean(link.can_freeze),
        password_protected: asBoolean(link.password_protected),
        protected_documents_included: asBoolean(link.protected_documents_included),
        views_count: numberOrZero(link.views_count),
        item_count: link.item_count === null || link.item_count === undefined
            ? null
            : numberOrZero(link.item_count),
        selection_mode: selectionMode,
    };
};

const sortLinks = (links) => [...links].sort((left, right) => {
    if (left.expired !== right.expired) return left.expired ? 1 : -1;
    const leftCreated = parseCaseDbDate(left.created_at).getTime() || 0;
    const rightCreated = parseCaseDbDate(right.created_at).getTime() || 0;
    return rightCreated - leftCreated;
});

const readJson = async (response) => {
    let payload = {};
    try {
        payload = await response.json();
    } catch (error) {
        throw new Error('CaseDB returned an unreadable response.');
    }
    if (!response.ok || payload.success === false) {
        throw new Error(payload.message || 'The request could not be completed.');
    }
    return payload;
};

const formatDateTime = (value) => {
    if (!value) return 'No expiration';
    const parsed = parseCaseDbDate(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatCreator = (createdBy) => {
    if (createdBy && typeof createdBy === 'object') {
        return createdBy.name || createdBy.user || createdBy.email || 'CaseDB user';
    }
    return String(createdBy || 'CaseDB user');
};

const getShareUrl = (link) => {
    const fallbackPath = link?.token ? `/share/${encodeURIComponent(link.token)}` : '';
    const value = link?.share_path || fallbackPath;
    if (!value) return '';
    try {
        return new URL(value, window.location.origin).toString();
    } catch (error) {
        return value;
    }
};

const getLinkKind = (link) => {
    if (link.kind === 'selection' || link.selection_mode === 'selection') return 'Selected items';
    return link.is_folder ? 'Folder' : 'File';
};

const getSelectionLabel = (link) => {
    if (!link.is_folder) return 'Shared file';
    if (link.kind === 'selection') {
        return link.selection_mode === 'snapshot' ? 'Fixed selected items' : 'Selected items';
    }
    if (link.selection_mode === 'live') return 'Live folder';
    if (link.selection_mode === 'snapshot') return 'Fixed file list';
    return 'Selected items';
};

const getItemName = (item = {}) => {
    if (item.name) return String(item.name);
    const source = String(item.relative_path || item.key || '').replace(/\/+$/, '');
    return source.split('/').pop() || 'Shared item';
};

const getItemFolder = (item = {}) => {
    if (item.folder !== undefined && item.folder !== null) {
        return String(item.folder || '—');
    }
    const relativePath = String(item.relative_path || '').replace(/^\/+|\/+$/g, '');
    const segments = relativePath.split('/').filter(Boolean);
    return segments.length > 1 ? segments.slice(0, -1).join(' / ') : '—';
};

const getItemShareDate = (item = {}) => (
    String(item.share_stage || '').toLowerCase() === 'added'
        ? formatDateTime(item.shared_at || item.source_last_modified)
        : 'Initial'
);

const CaseShareLinksModal = ({ caseId, onClose, onCreate, apiBase = defaultApiOrigin() }) => {
    const shareEndpoint = `${String(apiBase || '').replace(/\/+$/, '')}/share.php`;
    const [links, setLinks] = useState([]);
    const [selectedLinkId, setSelectedLinkId] = useState(null);
    const [itemsByLink, setItemsByLink] = useState({});
    const [loadingLinks, setLoadingLinks] = useState(true);
    const [linksError, setLinksError] = useState('');
    const [draft, setDraft] = useState({ allowUpload: false, includeFutureUploads: false });
    const [saving, setSaving] = useState(false);
    const [saveNotice, setSaveNotice] = useState(null);
    const [copiedLinkId, setCopiedLinkId] = useState(null);
    const linksRequestRef = useRef(0);
    const copyTimerRef = useRef(null);

    const selectedLink = useMemo(
        () => links.find(link => String(link.id) === String(selectedLinkId)) || null,
        [links, selectedLinkId],
    );

    const selectedItemsState = selectedLink
        ? itemsByLink[String(selectedLink.id)]
        : null;

    const loadLinks = useCallback(async (preferredId = null) => {
        const requestId = ++linksRequestRef.current;
        setLoadingLinks(true);
        setLinksError('');
        try {
            const response = await fetch(
                `${shareEndpoint}?action=case_links&case_id=${encodeURIComponent(caseId)}`,
                { credentials: 'include' },
            );
            const payload = await readJson(response);
            const nextLinks = sortLinks((Array.isArray(payload.data) ? payload.data : []).map(normalizeLink));
            if (requestId !== linksRequestRef.current) return nextLinks;

            setLinks(nextLinks);
            setSelectedLinkId(currentId => {
                const requestedId = preferredId === null ? currentId : preferredId;
                const stillExists = nextLinks.some(link => String(link.id) === String(requestedId));
                return stillExists ? requestedId : (nextLinks[0]?.id ?? null);
            });
            return nextLinks;
        } catch (error) {
            if (requestId === linksRequestRef.current) {
                setLinksError(error.message || 'CaseDB could not load these links.');
            }
            return null;
        } finally {
            if (requestId === linksRequestRef.current) setLoadingLinks(false);
        }
    }, [caseId, shareEndpoint]);

    const loadItems = useCallback(async (shareLinkId, { page = 1, append = false } = {}) => {
        const cacheKey = String(shareLinkId);
        setItemsByLink(current => ({
            ...current,
            [cacheKey]: {
                ...(current[cacheKey] || {}),
                loading: !append,
                loadingMore: append,
                loaded: append ? Boolean(current[cacheKey]?.loaded) : false,
                error: '',
            },
        }));
        try {
            // Reconciliation is intentionally a protected mutation. The GET
            // below is a read-only page fetch and never writes history while
            // holding a browser session open across S3 work.
            if (page === 1 && !append) {
                await ensureCaseDbCsrfToken();
                const syncResponse = await fetch(shareEndpoint, {
                    method: 'POST',
                    credentials: 'include',
                    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                    body: JSON.stringify({
                        action: 'sync_case_link_items',
                        case_id: caseId,
                        share_link_id: shareLinkId,
                    }),
                });
                await readJson(syncResponse);
            }
            const response = await fetch(
                `${shareEndpoint}?action=case_link_items&case_id=${encodeURIComponent(caseId)}&share_link_id=${encodeURIComponent(shareLinkId)}&page=${encodeURIComponent(page)}&per_page=100`,
                { credentials: 'include' },
            );
            const payload = await readJson(response);
            const data = Array.isArray(payload.data) ? payload.data : [];
            setItemsByLink(current => ({
                ...current,
                [cacheKey]: {
                    loading: false,
                    loadingMore: false,
                    loaded: true,
                    error: '',
                    data: append ? [...(current[cacheKey]?.data || []), ...data] : data,
                    itemCount: numberOrZero(payload.item_count ?? data.length),
                    page: numberOrZero(payload.page ?? page) || page,
                    hasMore: asBoolean(payload.has_more),
                },
            }));
            return data;
        } catch (error) {
            setItemsByLink(current => ({
                ...current,
                [cacheKey]: {
                    loading: false,
                    loadingMore: false,
                    loaded: true,
                    error: error.message || 'CaseDB could not load the shared items.',
                    data: [],
                    itemCount: 0,
                },
            }));
            return null;
        }
    }, [caseId, shareEndpoint]);

    useEffect(() => {
        loadLinks();
        return () => {
            linksRequestRef.current += 1;
        };
    }, [loadLinks]);

    useEffect(() => {
        if (!selectedLink) return;
        setDraft({
            allowUpload: selectedLink.allow_upload,
            includeFutureUploads: selectedLink.include_future_uploads,
        });
    }, [selectedLink]);

    useEffect(() => {
        setSaveNotice(null);
    }, [selectedLinkId]);

    useEffect(() => {
        if (!selectedLink) return;
        const itemState = itemsByLink[String(selectedLink.id)];
        if (!itemState?.loading && !itemState?.loaded) {
            loadItems(selectedLink.id);
        }
    }, [itemsByLink, loadItems, selectedLink]);

    useEffect(() => () => {
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    }, []);

    const selectLink = (linkId) => {
        if (saving) return;
        setSelectedLinkId(linkId);
    };

    const refresh = async () => {
        setSaveNotice(null);
        const currentId = selectedLink?.id ?? null;
        const refreshed = await loadLinks(currentId);
        if (currentId !== null && refreshed?.some(link => String(link.id) === String(currentId))) {
            await loadItems(currentId);
        }
    };

    const copyLink = async () => {
        if (!selectedLink) return;
        const url = getShareUrl(selectedLink);
        if (!url) return;
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard access is unavailable.');
            await navigator.clipboard.writeText(url);
            setCopiedLinkId(selectedLink.id);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopiedLinkId(null), 1800);
        } catch (error) {
            setSaveNotice({ type: 'error', text: 'CaseDB could not copy the link. Open it and copy the address from your browser.' });
        }
    };

    const canChangeFutureUploads = Boolean(
        selectedLink
        && !selectedLink.expired
        && selectedLink.is_folder
        && selectedLink.can_freeze,
    );
    const canChangeUploads = Boolean(
        selectedLink?.is_folder
        && !selectedLink.expired
        && (selectedLink.can_manage_uploads || draft.allowUpload),
    );
    const dirty = Boolean(selectedLink) && (
        draft.allowUpload !== selectedLink.allow_upload
        || draft.includeFutureUploads !== selectedLink.include_future_uploads
    );

    const saveChanges = async () => {
        if (!selectedLink || !dirty || selectedLink.expired || saving) return;
        setSaving(true);
        setSaveNotice(null);
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(shareEndpoint, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({
                    action: 'update_case_link',
                    case_id: caseId,
                    share_link_id: selectedLink.id,
                    allow_upload: selectedLink.is_folder ? draft.allowUpload : false,
                    include_future_uploads: canChangeFutureUploads
                        ? draft.includeFutureUploads
                        : selectedLink.include_future_uploads,
                }),
            });
            const payload = await readJson(response);
            const updatedLink = payload.link ? normalizeLink({ ...selectedLink, ...payload.link }) : null;
            if (updatedLink) {
                setLinks(current => sortLinks(current.map(link => (
                    String(link.id) === String(updatedLink.id) ? updatedLink : link
                ))));
            }
            setSaveNotice({
                type: 'success',
                text: payload.message || (
                    numberOrZero(payload.snapshot_count) > 0
                        ? `Changes saved with ${numberOrZero(payload.snapshot_count)} shared items.`
                        : 'Changes saved.'
                ),
            });
            await loadLinks(selectedLink.id);
            await loadItems(selectedLink.id);
        } catch (error) {
            setSaveNotice({ type: 'error', text: error.message || 'CaseDB could not save these changes.' });
        } finally {
            setSaving(false);
        }
    };

    const futureUploadsHint = (() => {
        if (!selectedLink?.is_folder) return '';
        if (selectedLink.expired) return 'Expired links cannot be changed.';
        if (selectedLink.selection_mode === 'snapshot') {
            return 'This link already has a fixed file list. Future uploads cannot be turned back on.';
        }
        if (selectedLink.selection_mode === 'selection') {
            return selectedLink.can_freeze
                ? 'Turn this off to keep only the files currently visible from the selected folders.'
                : 'Future upload visibility cannot be changed for this selected-items link.';
        }
        if (!selectedLink.can_freeze) return 'Future upload visibility cannot be changed for this link.';
        return 'Turn this off to keep only the files currently in the folder.';
    })();

    const recipientUploadsHint = (() => {
        if (!selectedLink?.is_folder) return '';
        if (selectedLink.expired) return 'Expired links cannot be changed.';
        if (selectedLink.can_manage_uploads) return 'Recipients can add files to this folder.';
        if (selectedLink.allow_upload) {
            return draft.allowUpload
                ? 'This older link can have upload access removed, but it cannot be turned back on.'
                : 'Upload access will be removed and cannot be turned back on for this older link.';
        }
        return 'This older link cannot be given upload access. Create a new link if recipients need to upload files.';
    })();

    const footer = (
        <div className="modal-footer-actions">
            <button type="button" className="action alt" onClick={onClose} disabled={saving}>Close</button>
        </div>
    );

    const header = (
        <div className="case-share-links-header-actions">
            {onCreate && (
                <div className="this-or-that-comp case-share-links-mode-switch" role="group" aria-label="Share link view">
                    <button
                        type="button"
                        className="tot-comp-option active"
                        aria-pressed="true"
                        title="View existing share links"
                    >
                        <Link size={16} />
                    </button>
                    <button
                        type="button"
                        className="tot-comp-option"
                        onClick={onCreate}
                        aria-pressed="false"
                        title="Create a new share link"
                        disabled={saving}
                    >
                        <Plus size={16} />
                    </button>
                </div>
            )}
            <button
                type="button"
                className="action alt small-text"
                onClick={refresh}
                disabled={loadingLinks || saving}
                title="Refresh share links"
            >
                <RefreshCw className={loadingLinks ? 'is-spinning' : ''} size={16} />
                {loadingLinks ? 'Refreshing' : 'Refresh'}
            </button>
        </div>
    );

    return (
        <Modal
            title="Share Links"
            instructions="Review links created for this case."
            onClose={saving ? undefined : onClose}
            wider={true}
            header={header}
            footer={footer}
        >
            <div className="modal-content-wrapper case-share-links-modal">
                {loadingLinks && links.length === 0 ? (
                    <div className="case-share-links-state" role="status">
                        <Loader2 className="is-spinning" size={20} />
                        <span>Loading share links…</span>
                    </div>
                ) : links.length === 0 && linksError ? (
                    <div className="case-share-links-state" role="alert">
                        <strong>Share links could not be loaded.</strong>
                        <span className="subtext">{linksError}</span>
                        <button type="button" className="action alt" onClick={() => loadLinks()}>Try Again</button>
                    </div>
                ) : links.length === 0 ? (
                    <div className="case-share-links-state">
                        <strong>No share links yet</strong>
                        <span className="subtext">Links created for this case will appear here.</span>
                    </div>
                ) : (
                    <>
                        {linksError && <div className="case-share-links-banner error" role="alert">{linksError}</div>}
                        <div className="case-share-links-layout">
                            <section className="case-share-links-list-panel" aria-label="Case share links">
                                <div className="case-share-links-panel-heading">
                                    <strong>Links</strong>
                                    <span>{links.length}</span>
                                </div>
                                <div className="case-share-links-list">
                                    {links.map(link => (
                                        <button
                                            type="button"
                                            key={link.id}
                                            className={`case-share-links-list-row${String(link.id) === String(selectedLinkId) ? ' selected' : ''}`}
                                            onClick={() => selectLink(link.id)}
                                            aria-label={`Select share link for ${link.location}`}
                                            aria-pressed={String(link.id) === String(selectedLinkId)}
                                        >
                                            <span className="case-share-links-row-topline">
                                                <span className={`case-share-links-status ${link.expired ? 'expired' : 'active'}`}>
                                                    {link.expired ? 'Expired' : 'Active'}
                                                </span>
                                                <span className="case-share-links-kind">{getLinkKind(link)}</span>
                                            </span>
                                            <strong className="case-share-links-location" title={link.location}>{link.location}</strong>
                                            <span className="case-share-links-row-meta">
                                                {link.expired ? 'Expired' : 'Expires'} {formatDateTime(link.expires_at)}
                                            </span>
                                            <span className="case-share-links-row-meta">
                                                {link.views_count} view{link.views_count === 1 ? '' : 's'} · {link.item_count === null
                                                    ? 'Open to load items'
                                                    : `${link.item_count} item${link.item_count === 1 ? '' : 's'}`}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </section>

                            {selectedLink && (
                                <section className="case-share-links-detail" aria-label="Selected share link details">
                                    <div className="case-share-links-detail-header">
                                        <div className="case-share-links-detail-title">
                                            <span className="case-share-links-detail-icon">
                                                {selectedLink.is_folder ? <Folder size={18} /> : <File size={18} />}
                                            </span>
                                            <div>
                                                <h3>{selectedLink.location}</h3>
                                                <span className="subtext">
                                                    {getSelectionLabel(selectedLink)} · Created by {formatCreator(selectedLink.created_by)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="case-share-links-detail-actions">
                                            <a
                                                className="action alt small-text"
                                                href={getShareUrl(selectedLink)}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <ExternalLink size={16} /> Open
                                            </a>
                                            <button type="button" className="action alt small-text" onClick={copyLink}>
                                                {String(copiedLinkId) === String(selectedLink.id)
                                                    ? <Check size={16} />
                                                    : <Copy size={16} />}
                                                {String(copiedLinkId) === String(selectedLink.id) ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="case-share-links-facts">
                                        <div><span>Status</span><strong>{selectedLink.expired ? 'Expired' : 'Active'}</strong></div>
                                        <div><span>Expires</span><strong>{formatDateTime(selectedLink.expires_at)}</strong></div>
                                        <div><span>Views</span><strong>{selectedLink.views_count}</strong></div>
                                        <div><span>Access</span><strong>{selectedLink.password_protected ? 'Password required' : 'Anyone with link'}</strong></div>
                                        <div><span>Protected content</span><strong>{selectedLink.protected_documents_included ? 'Included (warning confirmed)' : 'Excluded'}</strong></div>
                                    </div>
                                    {selectedLink.protected_documents_included && (
                                        <p className="subtext" role="note" style={{ color: 'var(--priority-color)' }}>
                                            This link includes protected content within its selection. Share it only with intended recipients.
                                            {selectedLink.protected_confirmed_at && ` Warning confirmed ${formatDateTime(selectedLink.protected_confirmed_at)}.`}
                                        </p>
                                    )}

                                    <div className="case-share-links-section">
                                        <div className="case-share-links-section-heading">
                                            <strong>Shared items</strong>
                                            <span>{selectedItemsState?.itemCount ?? selectedLink.item_count}</span>
                                        </div>
                                        <div className="case-share-links-items">
                                            {selectedItemsState?.loading ? (
                                                <div className="case-share-links-items-state" role="status">
                                                    <Loader2 className="is-spinning" size={18} /> Loading items…
                                                </div>
                                            ) : selectedItemsState?.error ? (
                                                <div className="case-share-links-items-state error" role="alert">
                                                    <span>{selectedItemsState.error}</span>
                                                    <button type="button" className="action alt small-text" onClick={() => loadItems(selectedLink.id)}>Try Again</button>
                                                </div>
                                            ) : selectedItemsState?.data?.length ? (
                                                <>
                                                    <table className="case-share-links-items-table">
                                                        <colgroup>
                                                            <col className="case-share-links-file-column" />
                                                            <col className="case-share-links-folder-column" />
                                                            <col className="case-share-links-date-column" />
                                                        </colgroup>
                                                        <thead>
                                                            <tr>
                                                                <th>File</th>
                                                                <th>Folder</th>
                                                                <th>Share Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedItemsState.data.map((item, index) => (
                                                                <tr key={item.key || `${item.relative_path}-${index}`}>
                                                                    <td>
                                                                        <span className="case-share-links-file-name" title={getItemName(item)}>
                                                                            <File size={15} />
                                                                            <strong>{getItemName(item)}</strong>
                                                                        </span>
                                                                    </td>
                                                                    <td title={getItemFolder(item)}>{getItemFolder(item)}</td>
                                                                    <td>{getItemShareDate(item)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {selectedItemsState.hasMore && (
                                                        <div className="case-share-links-load-more">
                                                            <button
                                                                type="button"
                                                                className="action alt small-text"
                                                                disabled={selectedItemsState.loadingMore}
                                                                onClick={() => loadItems(selectedLink.id, {
                                                                    page: (selectedItemsState.page || 1) + 1,
                                                                    append: true,
                                                                })}
                                                            >
                                                                {selectedItemsState.loadingMore && <Loader2 className="is-spinning" size={15} />}
                                                                {selectedItemsState.loadingMore ? 'Loading' : 'Load More'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="case-share-links-items-state">No files are currently included.</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="case-share-links-section settings">
                                        <div className="case-share-links-section-heading">
                                            <strong>Settings</strong>
                                        </div>
                                        {selectedLink.is_folder ? (
                                            <div className="case-share-links-settings-list">
                                                <div
                                                    className={`case-share-links-setting${canChangeUploads ? '' : ' disabled'}`}
                                                    role="group"
                                                    aria-label="Recipient upload settings"
                                                    aria-disabled={!canChangeUploads}
                                                >
                                                    <Checkbox
                                                        label="Allow recipient uploads"
                                                        value={draft.allowUpload}
                                                        onChange={canChangeUploads
                                                            ? () => setDraft(current => ({ ...current, allowUpload: !current.allowUpload }))
                                                            : () => {}}
                                                    />
                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>{recipientUploadsHint}</span>
                                                </div>
                                                <div
                                                    className={`case-share-links-setting${canChangeFutureUploads ? '' : ' disabled'}`}
                                                    role="group"
                                                    aria-label="Future upload settings"
                                                    aria-disabled={!canChangeFutureUploads}
                                                >
                                                    <Checkbox
                                                        label="Allow future uploads to appear in this link"
                                                        value={draft.includeFutureUploads}
                                                        onChange={canChangeFutureUploads
                                                            ? () => setDraft(current => ({
                                                                ...current,
                                                                includeFutureUploads: !current.includeFutureUploads,
                                                            }))
                                                            : () => {}}
                                                    />
                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>{futureUploadsHint}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="small-text" style={{ color: 'var(--subtext-color)' }}>This file link has no editable settings.</span>
                                        )}

                                        {saveNotice && (
                                            <div className={`case-share-links-save-notice ${saveNotice.type}`} role={saveNotice.type === 'error' ? 'alert' : 'status'}>
                                                {saveNotice.text}
                                            </div>
                                        )}
                                        {selectedLink.expired && (
                                            <div className="case-share-links-save-notice">Expired links cannot be changed.</div>
                                        )}
                                        <div className="case-share-links-save-actions">
                                            <button
                                                type="button"
                                                className="action"
                                                onClick={saveChanges}
                                                disabled={!dirty || selectedLink.expired || saving}
                                            >
                                                {saving && <Loader2 className="is-spinning" size={16} />}
                                                {saving ? 'Saving' : 'Save Changes'}
                                            </button>
                                        </div>
                                    </div>
                                </section>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default CaseShareLinksModal;
